import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import {
  getTimeSlots,
  getAllHostAvailability,
  setHostAvailability
} from '../firebase/appointmentService';
import { format } from 'date-fns';
import { parseDateLocal } from '../utils/dateUtils';
import './HostAvailability.css';

// Utility function to convert 24-hour time to 12-hour AM/PM format
const formatTime12Hour = (time24) => {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

function HostAvailability() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [timeSlots, setTimeSlots] = useState([]);
  const [myAvailability, setMyAvailability] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }
    loadData();
  }, [currentUser, navigate]);

  const loadData = async () => {
    setLoading(true);

    // Load time slots
    const slotsResult = await getTimeSlots();
    if (slotsResult.success) {
      setTimeSlots(slotsResult.timeSlots);
    }

    // Load my availability
    const availResult = await getAllHostAvailability();
    if (availResult.success) {
      const myAvail = availResult.availability
        .filter(a => a.hostId === (currentUser.uid || currentUser.id))
        .map(a => a.slotId);
      setMyAvailability(new Set(myAvail));
    }

    setLoading(false);
  };

  const handleToggleAvailability = async (slotId) => {
    setSaving(true);
    const hostId = currentUser.uid || currentUser.id;
    const isCurrentlyAvailable = myAvailability.has(slotId);
    const newAvailability = !isCurrentlyAvailable;

    const result = await setHostAvailability(hostId, slotId, newAvailability);

    if (result.success) {
      const newSet = new Set(myAvailability);
      if (newAvailability) {
        newSet.add(slotId);
      } else {
        newSet.delete(slotId);
      }
      setMyAvailability(newSet);
    } else {
      alert('Error updating availability. Please try again.');
    }

    setSaving(false);
  };

  const groupSlotsByDate = () => {
    const grouped = {};
    timeSlots.forEach(slot => {
      const date = parseDateLocal(slot.date);
      const dateKey = format(date, 'yyyy-MM-dd');
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: date,
          slots: []
        };
      }
      grouped[dateKey].slots.push(slot);
    });

    // Sort slots by time within each date
    Object.values(grouped).forEach(group => {
      group.slots.sort((a, b) => a.startTime.localeCompare(b.startTime));
    });

    return Object.values(grouped).sort((a, b) => a.date - b.date);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading availability...</p>
        </div>
      </DashboardLayout>
    );
  }

  const groupedSlots = groupSlotsByDate();

  return (
    <DashboardLayout>
      <div className="host-availability-container">
        <div className="availability-header">
          <h1>My Availability</h1>
          <p className="subtitle">Select the time slots when you are available for sessions</p>
        </div>

        {groupedSlots.length === 0 ? (
          <div className="empty-state">
            <h3>No Time Slots Available</h3>
            <p>Contact an administrator to add session time slots.</p>
          </div>
        ) : (
          <div className="availability-list">
            {groupedSlots.map((group, idx) => (
              <div key={idx} className="date-group">
                <h2 className="date-header">
                  {format(group.date, 'EEEE, MMMM d, yyyy')}
                </h2>
                <div className="slots-grid">
                  {group.slots.map(slot => {
                    const isAvailable = myAvailability.has(slot.id);
                    return (
                      <div
                        key={slot.id}
                        className={`slot-card ${isAvailable ? 'available' : 'unavailable'}`}
                        onClick={() => !saving && handleToggleAvailability(slot.id)}
                      >
                        <div className="slot-time">
                          {formatTime12Hour(slot.startTime)} - {formatTime12Hour(slot.endTime)}
                        </div>
                        <div className="slot-status">
                          {isAvailable ? (
                            <>
                              <span className="status-icon">✓</span>
                              <span className="status-text">Available</span>
                            </>
                          ) : (
                            <>
                              <span className="status-icon">○</span>
                              <span className="status-text">Unavailable</span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {saving && (
          <div className="saving-indicator">
            <div className="spinner-small"></div>
            <span>Saving...</span>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default HostAvailability;
